import React from "react";
import AppRoutes from "./routes";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/lib/utils";
import { MonthProvider } from "@/components/MonthContext";
import { useRoutePreloader } from "@/components/routing/RouteManager";
import { getIcon } from "@/components/utils/icons";
import UserAvatar from "@/components/layout/UserAvatar";
import AccountSwitcher from "@/components/layout/AccountSwitcher";
import ResponsiveNavigation from "@/components/navigation/ResponsiveNavigation";
import { useAuth } from '@/components/auth/AuthProvider';
import { useAccount } from "@/components/auth/AccountContext";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import ErrorBoundary, { CriticalComponentErrorBoundary } from "@/components/utils/ErrorBoundary";

// Header component otimizado com identidade visual e avatar do usuário
const AppHeader = React.memo(function AppHeader({ authUser }: { authUser: any }) {
  const { handleMouseEnter, handleFocus } = useRoutePreloader();
  const { currentAccountId } = useAccount();
  const { currentPlan } = useCurrentPlan();
  const WalletIcon = getIcon('Wallet');

  return (
    <header
      className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 sm:px-4 py-3 shadow-lg sticky top-0 z-50"
      style={{ willChange: 'transform' }}
      data-component="Header">

      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <Link
          to={createPageUrl("Planning")}
          className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-opacity"
          onMouseEnter={() => handleMouseEnter("/planning")}
          onFocus={() => handleFocus("/planning")}
          rel="prefetch"
          aria-label="Ir para página de planejamento - Bufunfa">

          {/* Ícone da Carteira */}
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center flex-shrink-0">
            <WalletIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          
          {/* Identidade Visual - Nome e Slogan */}
          <div className="flex flex-col justify-center min-w-0">
            {/* Nome do App - Sempre visível */}
            <div className="flex items-center">
              <strong className="text-base sm:text-lg lg:text-xl font-bold text-white tracking-tight">
                Bufunfa
              </strong>
            </div>
            
            {/* Slogan - Visível em telas pequenas e maiores */}
            <span className="block sm:text-sm text-xs text-emerald-100 font-medium leading-tight">
              Planejou, deu bom!
            </span>
          </div>
        </Link>
        
        {/* Área do Usuário - Avatar + Account Switcher + Informações contextuais */}
        {authUser && (
          <div className="flex items-center gap-2">
            {/* Informações contextuais - Visível apenas em telas médias e maiores */}
            <div className="hidden sm:flex flex-col items-end text-xs text-emerald-100 mr-2">
              {currentAccountId && (
                <div className="font-mono">
                  Conta: {currentAccountId}
                </div>
              )}
              {currentPlan && (
                <div className="font-mono">
                  Plano: {currentPlan.id}
                </div>
              )}
            </div>
            
            {/* Controle de Troca de Conta - Só aparece se tiver múltiplas contas */}
            <AccountSwitcher />
            
            {/* Avatar do Usuário */}
            <Link
              to={createPageUrl("/settings")}
              className="hover:opacity-80 transition-opacity"
              aria-label="Ir para configurações do perfil">

              <UserAvatar />
            </Link>
          </div>
        )}
      </div>
    </header>);

});

export default function Layout(): JSX.Element {
  const { user: authUser } = useAuth();
  const location = useLocation();
  
  // Verificar se está na página de login, auth callback ou landing page (que tem header próprio)
  const isAuthPage = location.pathname === '/login' || location.pathname === '/auth/callback' || location.pathname === '/reset-password' || location.pathname === '/';
  
  // Inicializar otimizações na montagem do Layout
  React.useEffect(() => {
    // Importar e inicializar otimizadores de forma segura
    Promise.all([
    import('@/components/optimization/CSSPurger').catch(() => ({ cssPurger: null })),
    import('@/components/optimization/CSSOptimizer').catch(() => ({ cssOptimizer: null })),
    import('@/components/optimization/NetworkOptimizer').catch(() => ({ networkOptimizer: null }))]
    ).then(([cssModule, cssOptimizerModule, networkModule]) => {
      // CSS Purger - CSS crítico imediato
      if (cssModule.cssPurger && cssModule.cssPurger.initialize) {
        cssModule.cssPurger.initialize();
      }

      // CSS Optimizer - Estilos de componente sob demanda
      if (cssOptimizerModule.cssOptimizer && cssOptimizerModule.cssOptimizer.initialize) {
        cssOptimizerModule.cssOptimizer.initialize();
      }

      // Network Optimizer - Preconnect e otimizações de rede
      if (networkModule.networkOptimizer && networkModule.networkOptimizer.initializeCriticalOptimizations) {
        networkModule.networkOptimizer.initializeCriticalOptimizations();

        // Otimizações não críticas depois
        setTimeout(() => {
          if (networkModule.networkOptimizer.initializeNonCriticalOptimizations) {
            networkModule.networkOptimizer.initializeNonCriticalOptimizations();
          }
        }, 1000);
      }
    }).catch((error) => {
      console.warn('Erro ao inicializar otimizadores:', error);
    });
  }, []);

  return (
    <ErrorBoundary 
      componentName="Layout Principal"
      showDetails={process.env.NODE_ENV === 'development'}
    >
      {isAuthPage ? (
        // Layout simples para páginas de autenticação sem MonthProvider
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col">
          <main className="flex-1 overflow-auto max-w-screen-xl mx-auto w-full pb-4">
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>}>
              <ErrorBoundary componentName="Conteúdo Principal">
                <AppRoutes />
              </ErrorBoundary>
            </React.Suspense>
          </main>
        </div>
      ) : (
        // Layout completo para páginas autenticadas com MonthProvider
        <MonthProvider>
          <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col">
            <CriticalComponentErrorBoundary componentName="Header">
              <AppHeader authUser={authUser} />
            </CriticalComponentErrorBoundary>
            <main className={`flex-1 overflow-auto max-w-screen-xl mx-auto w-full px-3 sm:px-4 ${authUser ? 'pb-20 sm:pb-64' : 'pb-4'}`}>
              <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen">
                <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>}>
                <ErrorBoundary componentName="Conteúdo Principal">
                  <AppRoutes />
                </ErrorBoundary>
              </React.Suspense>
            </main>
            {authUser && (
              <CriticalComponentErrorBoundary componentName="Navegação">
                <ResponsiveNavigation />
              </CriticalComponentErrorBoundary>
            )}
          </div>
        </MonthProvider>
      )}
    </ErrorBoundary>);

}
